import { useForm } from "@tanstack/react-form";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { resetPassword } from "@/auth/password";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { resetPasswordSchema } from "@/lib/schemas";

export const Route = createFileRoute("/_public/redefinir-senha")({
  // O token chega pela query do link enviado por e-mail (`?token=`).
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { token } = Route.useSearch();
  const router = useRouter();
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const form = useForm({
    defaultValues: { new_password: "", confirm_password: "" },
    validators: { onSubmit: resetPasswordSchema },
    onSubmit: async ({ value }) => {
      setFormError(null);
      const result = await resetPassword(token, value.new_password);
      if (result.ok) {
        setDone(true);
        return;
      }
      setFormError(describeResetError(result.code));
    },
  });

  // Sem token não há o que fazer aqui — normalmente é link colado pela metade.
  if (!token) {
    return (
      <FieldGroup>
        <div className="flex flex-col gap-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Link inválido</h1>
          <p className="text-sm text-muted-foreground">
            Abra o link direto do e-mail que enviamos, ou peça um novo.
          </p>
        </div>
        <FieldDescription className="text-center">
          <Link to="/esqueci-senha" className="underline underline-offset-4">
            Pedir um novo link
          </Link>
        </FieldDescription>
      </FieldGroup>
    );
  }

  if (done) {
    return (
      <FieldGroup>
        <div className="flex flex-col gap-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Senha alterada</h1>
          <p className="text-sm text-muted-foreground">
            Por segurança, todas as sessões abertas foram encerradas. Entre de novo com
            a senha nova.
          </p>
        </div>
        <Field>
          <Button
            type="button"
            onClick={() =>
              void router.navigate({ to: "/login", search: { redirect: "/" } })
            }
          >
            Ir para o login
          </Button>
        </Field>
      </FieldGroup>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      <FieldGroup>
        <div className="flex flex-col gap-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Nova senha</h1>
          <p className="text-sm text-muted-foreground">
            Escolha uma senha que você ainda não usou aqui.
          </p>
        </div>

        <form.Field name="new_password">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <FieldLabel htmlFor={field.name}>Nova senha</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                type="password"
                autoComplete="new-password"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                aria-invalid={field.state.meta.errors.length > 0}
              />
              <FieldError errors={field.state.meta.errors} />
              <FieldDescription>Pelo menos 8 caracteres.</FieldDescription>
            </Field>
          )}
        </form.Field>

        <form.Field name="confirm_password">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <FieldLabel htmlFor={field.name}>Repita a nova senha</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                type="password"
                autoComplete="new-password"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                aria-invalid={field.state.meta.errors.length > 0}
              />
              <FieldError errors={field.state.meta.errors} />
            </Field>
          )}
        </form.Field>

        {formError && (
          <p role="alert" className="text-sm text-destructive">
            {formError}
          </p>
        )}

        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Field>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Salvando…" : "Salvar nova senha"}
              </Button>
            </Field>
          )}
        </form.Subscribe>
      </FieldGroup>
    </form>
  );
}

function describeResetError(code: string): string {
  switch (code) {
    case "invalid_reset_token":
    case "reset_token_expired":
      return "Esse link já foi usado ou expirou. Peça um novo.";
    case "invalid_password":
      return "Senha fora do formato aceito (8 a 72 bytes).";
    default:
      return "Não foi possível alterar a senha. Tente novamente.";
  }
}
